const DEFAULT_TARGET_FPS = 30;

function createRenderHook (plot) {
  const hook = () => {
    const now = new Date().getTime();
    const delta = !plot.lastRenderTime ? 0 : now - plot.lastRenderTime;
    if (!plot.lastRenderTime || delta >= (1000 / plot.targetFps)) {
      if (plot.onUpdate) {
        plot.onUpdate(plot.lastRenderTime);
      }
      plot.update();
      plot.lastRenderTime = now;
    }
    if (plot.startTime) {
      requestAnimationFrame(hook);
    }
  };
  return hook;
}

function drawCurrentTimeAxis (canvas, maxTime, major, minor) {
  const ctx = canvas.getContext('2d');
  ctx.restore();
  const width = canvas.width;
  const height = canvas.height;
  if (width === 0 || height === 0) return;

  ctx.clearRect(0, 0, width, height);

  ctx.strokeStyle = 'rgba(0,0,0,0.5)';
  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.textAlign = 'center';
  ctx.font = '18px Arial';
  ctx.lineWidth = 2;

  // base axis line
  ctx.moveTo(0, 0);
  ctx.lineTo(width, 0);
  ctx.stroke();

  // draw minor ticks
  const ticks = maxTime / minor;
  const tickWidth = width / ticks;

  ctx.beginPath();
  for (let i = 0; i < ticks; i++) {
    const t = i * minor;
    const isMajor = t % major === 0;
    const x = width - (i * tickWidth);
    ctx.moveTo(x, 0);
    ctx.lineTo(x, isMajor ? 10 : 5);

    if (isMajor) {
      ctx.fillText(`-${(maxTime - (ticks - i)).toFixed(0)}`, x, 28);
    }
  }
  ctx.stroke();
}

export class BasePlot {
  constructor (el) {
    /**
     * The container element
     */
    this.el = el;
    /**
     * The target FPS for continuous rendering
     * @type {number}
     */
    this.targetFps = DEFAULT_TARGET_FPS;
    /**
     * The start time of the plot
     * @type {Number}
     */
    this.startTime = null;
    /**
     * Callback function that is triggered on plot updates. This can be used
     * for instance to update the current time
     *
     * @type {Function}
     */
    this.onUpdate = null;
    /**
     * The render hook that triggers updates on animation frames and tries
     * to fulfill the target FPS
     * @type {Function}
     */
    this.renderHook = createRenderHook(this);

    /**
     * The primary canvas
     * @type {HTMLCanvasElement}
     */
    this.canvas = document.createElement('canvas');
    /**
     * The primary canvas context
     * @type {CanvasRenderingContext2D | WebGLRenderingContext}
     */
    this.canvasCtx = this.canvas.getContext('2d');
    /**
     * The primary canvas width
     *
     * @type {number}
     */
    this.width = 0;
    /**
     * The primary canvas height
     *
     * @type {number}
     */
    this.height = 0;
    /**
     * The box height. We use this to track element resize
     * @type {number}
     */
    this.boxHeight = -1;
    /**
     * The box width. We use this to track element resize
     * @type {number}
     */
    this.boxWidth = -1;

    // Add the canvas to the container
    this.el.appendChild(this.canvas);
  }

  init () {
    // Init the size
    this.resize();
  }

  /**
   * Resize the canvas elements based on the size of the container
   */
  resize () {
    this.initCanvas_(this.canvas, true);
    this.width = this.canvas.width;
    this.height = this.canvas.height;
    this.boxWidth = this.el.offsetWidth;
    this.boxHeight = this.el.offsetHeight;
    // We resized, so lets make sure we fully restart rendering
    // last render time is an indicator that is used to do partial
    // rendering based on the elapsed time between two render calls
    this.lastRenderTime = null;
    this.draw();
  }

  /**
   * Start the rendering loop
   */
  start () {
    if (!this.startTime) {
      this.startTime = new Date().getTime();
      requestAnimationFrame(this.renderHook);
    }
  }

  /**
   * Stop the rendering loop
   */
  stop () {
    this.startTime = null;
  }

  /**
   * Implement this to draw the plot fully
   *
   * @abstract
   */
  draw () {

  }

  /**
   * Implement this to draw the plot on animation/rendering loop updates
   */
  update () {
    if (this.boxHeight !== this.el.offsetHeight ||
      this.boxWidth !== this.el.offsetWidth) {
      this.resize();
      return true;
    }
    return false;
  }

  /**
   * Init a canvas and set its size based on the parent element size
   *
   * @param canvas The canvas
   * @param updateStyle If true we also update the style
   * @param height The target height. If not given the container height is used
   * @protected
   */
  initCanvas_ (canvas, updateStyle, height) {
    // height = height || Math.min(
    //   this.el.parentElement.clientHeight,
    //   this.el.offsetHeight);
    height = height || this.el.parentElement.clientHeight;
    const scale = 2; // window.devicePixelRatio;
    canvas.width = this.el.offsetWidth * scale;
    canvas.height = height * scale;

    if (updateStyle) {
      canvas.style.width = this.el.offsetWidth + 'px';
      canvas.style.height = height + 'px';
    }
  }
}

export class Donut extends BasePlot {
  constructor (el) {
    super(el);
    /**
     * The margin offset for the donut
     * @type {number}
     */
    this.offset = 20;
    /**
     * The width of the donut
     * @type {number}
     */
    this.size = 50;
    /**
     * We store the data here. The data have the form `{color: '', value: number}`
     * @type {Array}
     */
    this.data = [];

    /**
     * The internal buffer
     * @type {HTMLCanvasElement}
     */
    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
  }

  setData (data) {
    this.data = data;
    this.draw();
  }

  resize () {
    this.initCanvas_(this.buffer);
    super.resize();
  }

  draw () {
    this.update();
  }

  update () {
    const ctx = this.bufferCtx;
    ctx.clearRect(0, 0, this.width, this.height);

    const w = Math.min(this.width, this.height);
    const r = (w - this.offset) / 2;

    const fillRadius = 2 * Math.PI;
    let sum = 0;
    this.data.forEach((d) => {
      sum += d.value;
    });

    let start = -(Math.PI / 2);
    let v = 0;
    this.data.forEach(d => {
      v = d.value / sum;
      ctx.fillStyle = d.color;
      const end = start + (fillRadius * v);

      ctx.beginPath();
      ctx.moveTo(w / 2, w / 2);
      ctx.arc(w / 2, w / 2, r, start, end);
      ctx.closePath();
      ctx.fill();
      start = end;
    });

    // clear the inner part
    ctx.save();
    ctx.fillStyle = '#FFFFFF';
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.moveTo(w / 2, w / 2);
    ctx.arc(w / 2, w / 2, r - this.size, 0, 2 * Math.PI);
    ctx.fill();
    ctx.restore();

    const c = this.canvasCtx;
    c.clearRect(0, 0, this.width, this.height);
    c.drawImage(this.buffer, 0, 0);
    this.lastRenderTime = new Date().getTime();

    const mainCtx = this.canvasCtx;
    mainCtx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    mainCtx.drawImage(this.buffer, 0, 0);
  }
}

export class Series {
  constructor () {
    this.data = [];
    this.fillStyle = 'rgba(255, 165, 0, 0.25)';
    this.strokeStyle = 'rgba(255, 165, 0, 1)';

    this.maxTime = 0;
    this.maxValue = 0;
  }

  add (value, time) {
    if (!time) {
      time = new Date().getTime();
    }
    this.data.push([time, value]);
    this.maxTime = Math.max(time, this.maxTime);
    this.maxValue = Math.max(value, this.maxValue);
    return time;
  }

  purge (startTime) {
    let spliceIndex = -1;
    let updateMaxValues = false;
    for (let i = 0; i < this.data.length - 1; i++) {
      const d = this.data[i];
      const d_next = this.data[i + 1];
      if (d_next[0] < startTime) {
        spliceIndex = i;
        updateMaxValues = d[0] >= this.maxTime || d[1] >= this.maxValue;
      } else {
        break;
      }
    }

    if (spliceIndex >= 0) {
      this.data.splice(0, spliceIndex + 1);
    }

    if (updateMaxValues) {
      this.maxTime = 0;
      this.maxValue = 0;
      this.data.forEach(d => {
        this.maxTime = Math.max(d[0], this.maxTime);
        this.maxValue = Math.max(d[1], this.maxValue);
      });
    }
  }
}

export class AbrPlot extends BasePlot {
  constructor (el, drawXAxis = true) {
    super(el);
    this.maxTime = 60000;
    this.series = [];
    this.renditions = [];
    this.maxRendition = 0;
    this.drawXAxis = drawXAxis;

    this.xAxis = {
      offset: drawXAxis ? 30 : 0,
      minorHeight: 5,
      majorHeight: 10
    };

    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
    this.xLinesCanvas = document.createElement('canvas');
    this.xLinesCanvasCtx = this.xLinesCanvas.getContext('2d');
    this.xAxisCanvas = document.createElement('canvas');
  }

  resize () {
    this.initCanvas_(this.buffer);
    this.initCanvas_(this.xLinesCanvas);
    if (this.drawXAxis) {
      this.initCanvas_(this.xAxisCanvas, false, this.xAxis.offset);
    }
    super.resize();
  }

  addSeries (series) {
    this.series.push(series);
  }

  draw () {
    if (this.drawXAxis) {
      this.updateXAxis();
    }
    this.updateGridLines();
    this.update();
  }

  update () {
    if (super.update()) {
      return;
    }

    this.updatePlot();
    const ctx = this.canvasCtx;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.width > 0 && this.height > 0) {
      // draw buffers
      ctx.drawImage(this.buffer, 0, 0);
      if (this.drawXAxis) {
        ctx.drawImage(this.xAxisCanvas, 0, this.height - this.xAxis.offset);
      }
      ctx.drawImage(this.xLinesCanvas, 0, 0);
    }
  }

  updatePlot () {
    if (this.width <= 0 || this.height <= 0) return;
    const now = new Date().getTime();
    const offsetY = this.xAxis.offset;
    const offsetX = 0;
    let maxY = this.maxRendition;
    const timeRight = now;
    const timeLeft = now - this.maxTime;
    const height = this.height - offsetY;
    const width = this.width - offsetX;

    const pxPerMs = width / this.maxTime;
    const ctx = this.bufferCtx;
    ctx.restore();
    ctx.clearRect(0, 0, this.width, this.height);

    const bandwidthToRendition = {};
    this.renditions.forEach((r, i) => {
      bandwidthToRendition[r.value] = i;
    });
    const heightPerLine = height / this.renditions.length;

    // render series
    this.series.forEach(s => {
      maxY = maxY || s.maxValue;
      ctx.fillStyle = s.fillStyle;
      ctx.strokeStyle = s.strokeStyle;
      ctx.lineWidth = 6;

      s.purge(timeLeft);

      ctx.beginPath();
      let lx = null;

      for (let i = s.data.length - 1; i >= 0; i--) {
        const d = s.data[i];
        const t_l = timeRight - d[0];

        const x_l = Math.floor(width - (t_l * pxPerMs));
        const y = height - ((bandwidthToRendition[d[1]] + 1) * heightPerLine);

        if (i === s.data.length - 1) {
          ctx.moveTo(offsetX + width, y);
        }

        if (lx != null && lx >= 0) {
          ctx.lineTo(lx, y);
        }
        ctx.lineTo(x_l, y);

        lx = x_l;
      }

      ctx.stroke();

      // extend the last point back to origin to get a good fill
      if (lx != null) {
        ctx.lineTo(lx, height);
        ctx.lineTo(width, height);
      }

      ctx.fill();

      ctx.closePath();
    });
  }

  updateGridLines () {
    const ctx = this.xLinesCanvasCtx;
    const width = this.width;
    const height = this.height - this.xAxis.offset;
    ctx.clearRect(0, 0, width, height);
    if (width === 0 || height <= 0) return;

    const heightPerLine = height / this.renditions.length;
    const ox = 0;
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';

    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = Math.min(18, heightPerLine) + 'px Arial';
    ctx.setLineDash([10]);

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    this.renditions.forEach((l, i) => {
      const y = height - ((i + 1) * heightPerLine);
      ctx.moveTo(ox, y);
      ctx.lineTo(width - ox, y);
      ctx.fillText(l.label, ox + 10, y + (heightPerLine / 2));
    });
    ctx.stroke();
  }

  updateXAxis () {
    drawCurrentTimeAxis(this.xAxisCanvas, this.maxTime / 1000, 5, 1);
  }
}

export class BufferPlot extends BasePlot {
  constructor (el) {
    super(el);
    /**
     * The minimum buffer size in seconds. This defines the
     * minimum x axis range for the plot.
     * @type {number}
     */
    this.minBufferSize = 30;
    /**
     * The renditions
     *
     * @type {Array}
     */
    this.renditions = [];
    /**
     * The maximum segment height in px
     * @type {number}
     */
    this.maxSegmentHeight = 30;
    /**
     * The buffer segments
     * @type {Array}
     */
    this.segments = [];
    /**
     * The smallest segment start time
     *
     * @type {number}
     */
    this.segmentMin = 0;
    /**
     * The largest segment end time
     * @type {number}
     */
    this.segmentMax = 0;
    /**
     * The buffer plot canvas
     * @type {HTMLCanvasElement}
     */
    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
    /**
     * The rendition lines canvas
     * @type {HTMLCanvasElement}
     */
    this.xLinesCanvas = document.createElement('canvas');
    this.xLinesCanvasCtx = this.xLinesCanvas.getContext('2d');
    /**
     * The current time of the player to render the current time marker
     * @type {number}
     */
    this.currentTime = -1;
  }

  resize () {
    this.initCanvas_(this.buffer);
    this.initCanvas_(this.xLinesCanvas);
    super.resize();
  }

  draw () {
    this.updateGridLines();
    this.updatePlot();
    this.update();
  }

  update () {
    if (super.update()) {
      return;
    }
    const ctx = this.canvasCtx;
    const width = this.width;
    const height = this.height;

    ctx.clearRect(0, 0, width, height);
    if (this.buffer && width !== 0 && height !== 0) {
      ctx.drawImage(this.buffer, 0, 0);
    }
    if (this.xLinesCanvas && width !== 0 && height !== 0) {
      ctx.drawImage(this.xLinesCanvas, 0, 0);
    }
    this.updateCurrentTime();
  }

  updateSegmentBounds () {
    this.segmentMax = 0;
    this.segmentMin = Number.MAX_SAFE_INTEGER;
    this.segments.forEach(s => {
      this.segmentMin = Math.min(this.segmentMin, s.start);
      this.segmentMax = Math.max(this.segmentMax, s.end);
    });
  }

  updateCurrentTime () {
    if (this.currentTime >= 0) {
      const ctx = this.canvasCtx;
      const width = this.width;
      const height = this.height;
      ctx.strokeStyle = '#FF0000';
      ctx.fillStyle = '#FF0000';

      let range = this.segmentMax - this.segmentMin;
      range = Math.max(this.minBufferSize, range);
      const x = Math.floor(width * ((this.currentTime - this.segmentMin) / range));

      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, height);
      ctx.stroke();
    }
  }

  updatePlot () {
    if (!this.bufferCtx) return;
    const ctx = this.bufferCtx;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.width <= 0 || this.height <= 0) return;

    if (this.segments.length === 0) return;

    const height = this.height;
    const width = this.width;
    const heightPerLine = height / this.renditions.length;
    const elementHeight = Math.min(heightPerLine, this.maxSegmentHeight);
    // TODO get the style
    ctx.fillStyle = 'rgba(25,127,171,0.5)';
    ctx.strokeStyle = '#FFFFFF';

    this.updateSegmentBounds();
    let range = this.segmentMax - this.segmentMin;
    range = Math.max(this.minBufferSize, range);
    const labels = [];
    let row_height = 0;
    this.segments.forEach(d => {
      const x_s = Math.ceil(width * ((d.start - this.segmentMin) / range));
      const x_e = Math.ceil(width * ((d.end - this.segmentMin) / range));
      const i = this.renditions.findIndex(v => v.value === d.value);
      const y_s = Math.floor(height - ((i + 1) * heightPerLine));
      const y_e = y_s + heightPerLine;
      row_height = y_e - y_s;
      const y = y_s + (row_height / 2) - (elementHeight / 2);
      const x = x_s;

      ctx.beginPath();
      ctx.rect(x, y, x_e - x_s, elementHeight);
      ctx.fill();
      ctx.stroke();
      ctx.closePath();

      labels.push({
        label: d.text,
        x: x_e - 10,
        y: y_s + (row_height / 2) + 1
      });
    });

    ctx.beginPath();
    ctx.moveTo(0, height);
    // todo label style
    ctx.strokeStyle = '#FFFFFF';
    ctx.fillStyle = '#FFFFFF';
    ctx.font = Math.min(18, row_height) + 'px Arial';
    ctx.textAlign = 'right';
    ctx.textBaseline = 'middle';
    labels.forEach(l => {
      ctx.fillText(l.label, l.x, l.y);
    });
    ctx.stroke();
    ctx.closePath();
  }

  updateGridLines () {
    if (!this.renditions) return;
    const ctx = this.xLinesCanvasCtx;
    const width = this.width;
    const height = this.height;
    const heightPerLine = height / this.renditions.length;
    const ox = 0;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.font = Math.min(18, heightPerLine) + 'px Arial';
    ctx.setLineDash([10]);

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    this.renditions.forEach((l, i) => {
      const y = Math.floor(height - ((i + 1) * heightPerLine));
      ctx.moveTo(ox, y);
      ctx.lineTo(width - ox, y);
      ctx.fillText(l.label, ox + 10, y + (heightPerLine / 2));
    });
    ctx.stroke();
  }
}

export class BufferLinePlot extends BasePlot {
  constructor (el) {
    super(el);
    this.maxTime = 60000;
    this.bufferSamples = [];
    this.maxValue = 0;
    this.bufferAhead = 0;

    this.xAxis = {
      offset: 30,
      minorHeight: 5,
      majorHeight: 10
    };

    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
    this.xAxisCanvas = document.createElement('canvas');
    this.xAxisCanvasCtx = this.xAxisCanvas.getContext('2d');

    this.xLinesCanvas = document.createElement('canvas');
    this.xLinesCanvasCtx = this.xLinesCanvas.getContext('2d');
  }

  addSample (ahead) {
    const now = new Date().getTime();
    this.bufferSamples.push({
      time: now,
      value: this.bufferAhead
    });
    this.bufferSamples.push({
      time: now,
      value: ahead
    });

    let spliceIndex = -1;
    const startTime = now - this.maxTime;
    let updateMaxValues = ahead > this.maxValue;
    for (let i = 0; i < this.bufferSamples.length - 1; i++) {
      const d = this.bufferSamples[i];
      const d_next = this.bufferSamples[i + 1];
      if (d_next.time < startTime) {
        spliceIndex = i;
        updateMaxValues = d.value >= this.maxValue;
      } else {
        break;
      }
    }

    if (spliceIndex >= 0) {
      this.bufferSamples.splice(0, spliceIndex + 1);
    }

    if (updateMaxValues || this.maxValue === 0) {
      // TODO: Use max buffer level from player here and preserve the prevous max
      this.maxValue = 0;

      this.bufferSamples.forEach(s => {
        this.maxValue = Math.max(s.value, this.maxValue);
        this.lastRenderTime = null;
      });
    }
  }

  resize () {
    this.initCanvas_(this.buffer);
    this.initCanvas_(this.xLinesCanvas);
    this.initCanvas_(this.xAxisCanvas, false, this.xAxis.offset);
    super.resize();
  }

  draw () {
    this.updateXAxis();
    this.update();
  }

  update () {
    if (super.update()) {
      return;
    }

    if (this.lastRenderTime === null) {
      this.updateGridLines();
    }
    this.updatePlot();
    const ctx = this.canvasCtx;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.width > 0 && this.height > 0) {
      ctx.drawImage(this.buffer, 0, 0);
      ctx.drawImage(this.xAxisCanvas, 0, this.height - this.xAxis.offset);
      ctx.drawImage(this.xLinesCanvas, 0, 0);
    }
  }

  updatePlot () {
    if (this.width <= 0 || this.height <= 0) {
      return;
    }

    const now = new Date().getTime();
    const offsetY = this.xAxis.offset;
    const offsetX = 0;
    const timeRight = now;
    const height = this.height - offsetY;
    const width = this.width - offsetX;

    const pxPerMs = width / this.maxTime;
    const ctx = this.bufferCtx;
    ctx.clearRect(0, 0, this.width, this.height);

    ctx.strokeStyle = 'rgb(25,127,171)';
    ctx.fillStyle = 'rgba(25,127,171,0.5)';

    // Move the position to the durrent buffer head on the right
    const bufferAhead_y = height - (height * (this.bufferAhead / this.maxValue));
    ctx.beginPath();
    ctx.moveTo(offsetX + width, bufferAhead_y);
    let lx = offsetX + width;
    for (let i = this.bufferSamples.length - 1; i >= 0; i--) {
      const s = this.bufferSamples[i];
      const t_l = timeRight - s.time;
      const x_l = Math.floor(width - (t_l * pxPerMs));
      const y = height - (height * (s.value / this.maxValue));

      ctx.lineTo(x_l, y);
      lx = x_l;
    }
    ctx.stroke();

    // extend the last point back to origin to get a good fill
    if (lx != null) {
      ctx.lineTo(lx, height);
      ctx.lineTo(width, height);
    }

    ctx.fill();
    ctx.closePath();
  }

  updateGridLines () {
    const ctx = this.xLinesCanvasCtx;
    const width = this.width;
    const height = this.height - this.xAxis.offset;
    ctx.clearRect(0, 0, width, height);
    if (width === 0 || height <= 0) return;

    const heightPerLine = height / this.maxValue;
    const lines = Math.floor(this.maxValue / 5);
    const ox = 0;
    ctx.strokeStyle = 'rgba(0,0,0, 0.5)';
    ctx.fillStyle = 'rgba(0,0,0, 0.5)';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    const fontHeight = Math.min(18, Math.ceil(height / lines));
    ctx.font = fontHeight + 'px Arial';
    ctx.setLineDash([10]);

    ctx.clearRect(0, 0, width, height);
    ctx.beginPath();
    ctx.beginPath();
    let stepSize = 5;
    while (Math.floor(this.maxValue / stepSize) > 5) {
      stepSize *= 2;
    }
    for (let i = 0; i < this.maxValue; i += stepSize) {
      const y = height - ((i) * heightPerLine);
      if (y < height) {
        ctx.moveTo(ox, y);
        ctx.lineTo(width - ox, y);
        ctx.fillText(`${i} seconds`, ox + 10, y + (fontHeight / 2) + 2);
      }
    }
    ctx.stroke();
  }

  updateXAxis () {
    drawCurrentTimeAxis(this.xAxisCanvas, this.maxTime / 1000, 5, 1);
  }
}

export class StatePlot extends BasePlot {
  constructor (el, drawXAxis = true) {
    super(el);
    this.maxTime = 60000;
    this.states = [];
    this.drawXAxis = drawXAxis;
    this.xAxis = {
      offset: drawXAxis ? 30 : 0,
      minorHeight: 5,
      majorHeight: 10
    };

    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
    this.xAxisCanvas = document.createElement('canvas');
  }

  addSample (state) {
    const now = new Date().getTime();
    this.states.push({
      time: now,
      value: state
    });

    let spliceIndex = -1;
    const startTime = now - this.maxTime;
    for (let i = 0; i < this.states.length - 2; i++) {
      const d_next = this.states[i + 1];
      if (d_next.time < startTime) {
        spliceIndex = i;
      } else {
        break;
      }
    }

    if (spliceIndex >= 0) {
      this.states.splice(0, spliceIndex + 1);
    }
  }

  resize () {
    this.initCanvas_(this.buffer);
    this.initCanvas_(this.xAxisCanvas, false, this.xAxis.offset);
    super.resize();
  }

  draw () {
    if (this.drawXAxis) {
      this.updateXAxis();
    }
    this.update();
  }

  update () {
    if (super.update()) {
      return;
    }

    this.updatePlot();
    const ctx = this.canvasCtx;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.width > 0 && this.height > 0) {
      ctx.drawImage(this.buffer, 0, 0);
      if (this.drawXAxis) {
        ctx.drawImage(this.xAxisCanvas, 0, this.height - this.xAxis.offset);
      }
    }
  }

  updatePlot () {
    if (this.width <= 0 || this.height <= 0) {
      return;
    }

    const now = new Date().getTime();
    const offsetY = this.xAxis.offset;
    const offsetX = 0;
    const timeRight = now;
    const height = this.height - offsetY;
    const width = this.width - offsetX;

    const pxPerMs = width / this.maxTime;

    const ctx = this.bufferCtx;
    ctx.clearRect(0, 0, this.width, this.height);

    const colors = {};
    colors[clpp.Player.State.IDLE] = {
      stroke: '#befffa'
    };
    colors[clpp.Player.State.PREPARING] = {
      stroke: '#00cdff'
    };
    colors[clpp.Player.State.BUFFERING] = {
      stroke: '#ffb55c'
    };
    colors[clpp.Player.State.PLAYING] = {
      stroke: '#64ff72'
    };
    colors[clpp.Player.State.PAUSED] = {
      stroke: '#fffb00'
    };
    colors[clpp.Player.State.ENDED] = {
      stroke: '#7dfffc'
    };
    colors[clpp.Player.State.ERROR] = {
      stroke: '#ff0705'
    };

    ctx.strokeStyle = '#ff6966';
    ctx.fillStyle = '#2a61ff';

    // Move the position to the current buffer head on the right

    let offset = this.xAxis.offset;
    let elementHeight = Math.max(height - offset, 0);
    if (elementHeight === 0) {
      elementHeight = height;
      offset = 0;
    }
    let lx = offsetX + width;
    for (let i = this.states.length - 1; i >= 0; i--) {
      const s = this.states[i];
      const t_l = timeRight - s.time;
      const x_l = Math.floor(width - (t_l * pxPerMs));
      const y = (offset / 2);

      if (colors[s.value]) {
        ctx.strokeStyle = colors[s.value].stroke;
        ctx.fillStyle = colors[s.value].stroke;
      } else {
        ctx.strokeStyle = '#ff6966';
        ctx.fillStyle = '#ff6966';
      }

      ctx.beginPath();
      if (s.value === clpp.Player.State.BUFFERING) {
        ctx.save();
        ctx.setLineDash([10]);
        ctx.moveTo(x_l, y + ((elementHeight) / 2));
        ctx.lineTo(lx, y + ((elementHeight) / 2));
        ctx.stroke();
        ctx.restore();
      } else {
        ctx.rect(x_l, y, lx - x_l, elementHeight);
        ctx.globalAlpha = 0.5;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.stroke();
      }
      ctx.closePath();
      lx = x_l;
    }
  }

  updateXAxis () {
    drawCurrentTimeAxis(this.xAxisCanvas, this.maxTime / 1000, 5, 1);
  }
}

export class DowloadsPlot extends BasePlot {
  constructor (el) {
    super(el);
    this.maxTime = 60000;
    this.samples = [];

    this.xAxis = {
      offset: 30,
      minorHeight: 5,
      majorHeight: 10
    };

    this.buffer = document.createElement('canvas');
    this.bufferCtx = this.buffer.getContext('2d');
    this.xAxisCanvas = document.createElement('canvas');
  }

  addSample (state) {
    const now = new Date().getTime();
    this.states.push({
      time: now,
      value: state
    });

    let spliceIndex = -1;
    const startTime = now - this.maxTime;
    for (let i = 0; i < this.states.length - 2; i++) {
      const d_next = this.states[i + 1];
      if (d_next.time < startTime) {
        spliceIndex = i;
      } else {
        break;
      }
    }

    if (spliceIndex >= 0) {
      this.states.splice(0, spliceIndex + 1);
    }
  }

  resize () {
    this.initCanvas_(this.buffer);
    this.initCanvas_(this.xAxisCanvas, false, this.xAxis.offset);
    super.resize();
  }

  draw () {
    this.updateXAxis();
    this.update();
  }

  update () {
    if (super.update()) {
      return;
    }

    this.updatePlot();
    const ctx = this.canvasCtx;
    ctx.clearRect(0, 0, this.width, this.height);
    if (this.width > 0 && this.height > 0) {
      ctx.drawImage(this.buffer, 0, 0);
      ctx.drawImage(this.xAxisCanvas, 0, this.height - this.xAxis.offset);
    }
  }

  updatePlot () {
    if (this.width <= 0 || this.height <= 0) {
      return;
    }

    const ctx = this.bufferCtx;
    ctx.clearRect(0, 0, this.width, this.height);

    ctx.strokeStyle = '#ff6966';
    ctx.fillStyle = '#2a61ff';
  }

  updateXAxis () {
    drawCurrentTimeAxis(this.xAxisCanvas, this.maxTime / 1000, 5, 1);
  }
}
